import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getQuoteyUpdates,
  addQuoteyUpdate,
  updateQuoteyUpdate,
  getQuoteyRecentUpdates,
  deleteQuoteyUpdate,
} from "../../store/components";
import "./WhatsNewDashboard.css"; // Ensure to link the CSS file

const WhatsNewDashboard = () => {
  const dispatch = useDispatch();
  const updates = useSelector(getQuoteyUpdates);
  const [whatsNewUpdates, setWhatsNewUpdates] = useState(updates);
  const [editMode, setEditMode] = useState(false);
  const [currentUpdateId, setCurrentUpdateId] = useState(null);

  // Updated initial form state with bilingual title and description
  const initialFormState = {
    version: "",
    details: [
      {
        title: { en: "", fr: "" },
        description: { en: "", fr: "" },
        type: "",
      },
    ],
  };
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    dispatch(getQuoteyRecentUpdates());
  }, [dispatch]);

  useEffect(() => {
    setWhatsNewUpdates(updates);
  }, [updates]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  /**
   * Updated handleDetailChange to support bilingual fields.
   * If a `lang` parameter is provided, it updates that language key (e.g., "en" or "fr")
   * for the given field ("title" or "description"). Otherwise, it updates a simple field (like "type").
   */
  const handleDetailChange = (e, index, field, lang = null) => {
    const { value } = e.target;
    const updatedDetails = formData.details.map((detail, i) => {
      if (i === index) {
        if (lang) {
          // Update the bilingual field (title or description) for the specific language.
          return {
            ...detail,
            [field]: {
              ...detail[field],
              [lang]: value,
            },
          };
        } else {
          // Update a non-bilingual field (like type)
          return { ...detail, [field]: value };
        }
      }
      return detail;
    });
    setFormData({ ...formData, details: updatedDetails });
  };

  const addDetail = () => {
    setFormData({
      ...formData,
      details: [
        ...formData.details,
        {
          title: { en: "", fr: "" },
          description: { en: "", fr: "" },
          type: "",
        },
      ],
    });
  };

  const removeDetail = (index) => {
    const details = [...formData.details];
    details.splice(index, 1);
    setFormData({ ...formData, details });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editMode) {
      dispatch(updateQuoteyUpdate(currentUpdateId, formData));
    } else {
      dispatch(addQuoteyUpdate(formData));
    }
    setFormData(initialFormState);
    setEditMode(false);
    setCurrentUpdateId(null);
  };

  const handleEdit = (update) => {
    setFormData(update);
    setEditMode(true);
    setCurrentUpdateId(update._id);
  };

  const handleDelete = (id) => {
    dispatch(deleteQuoteyUpdate(id)).then(() => {
      setWhatsNewUpdates((currentUpdates) =>
        currentUpdates.filter((update) => update._id !== id)
      );
    });
  };

  return (
    <div className="whats-new-dashboard">
      <h2 className="dashboard-title">What's New Dashboard</h2>
      <form className="whats-new-form" onSubmit={handleSubmit}>
        <input
          type="text"
          name="version"
          value={formData.version}
          onChange={handleChange}
          placeholder="Version"
        />
        {formData.details.map((detail, index) => (
          <div
            style={{
              padding: "10px",
              border: "1px solid #ccc",
              marginBottom: "10px",
            }}
            key={index}
          >
            {/* English Title */}
            <input
              type="text"
              name="title_en"
              value={detail.title.en}
              onChange={(e) => handleDetailChange(e, index, "title", "en")}
              placeholder="Detail Title (EN)"
            />
            {/* French Title */}
            <input
              type="text"
              name="title_fr"
              value={detail.title.fr}
              onChange={(e) => handleDetailChange(e, index, "title", "fr")}
              placeholder="Detail Title (FR)"
            />
            {/* English Description */}
            <textarea
              name="description_en"
              value={detail.description.en}
              onChange={(e) =>
                handleDetailChange(e, index, "description", "en")
              }
              placeholder="Detail Description (EN)"
            />
            {/* French Description */}
            <textarea
              name="description_fr"
              value={detail.description.fr}
              onChange={(e) =>
                handleDetailChange(e, index, "description", "fr")
              }
              placeholder="Detail Description (FR)"
            />
            {/* Type remains the same for both languages */}
            <select
              name="type"
              value={detail.type}
              onChange={(e) => handleDetailChange(e, index, "type")}
            >
              <option value="">Select Type</option>
              <option value="Feature">Feature</option>
              <option value="Bugfix">Bugfix</option>
            </select>
            <button type="button" onClick={() => removeDetail(index)}>
              Remove Detail
            </button>
          </div>
        ))}
        <button type="button" onClick={addDetail}>
          Add Another Detail
        </button>
        <button type="submit">
          {editMode ? "Save Changes" : "Add New Update"}
        </button>
      </form>
      <ul className="whats-new-list">
        {whatsNewUpdates.map((update) => (
          <li className="update-item" key={update._id}>
            <div>
              <strong>Version:</strong> {update.version}
            </div>
            {update.details.map((detail, index) => (
              <div
                key={index}
                style={{
                  marginTop: "5px",
                  padding: "5px",
                  border: "1px dashed #ddd",
                }}
              >
                <div>
                  <strong>Title (EN):</strong> {detail.title.en}
                </div>
                <div>
                  <strong>Title (FR):</strong> {detail.title.fr}
                </div>
                <div>
                  <strong>Description (EN):</strong> {detail.description.en}
                </div>
                <div>
                  <strong>Description (FR):</strong> {detail.description.fr}
                </div>
                <div>
                  <strong>Type:</strong> {detail.type}
                </div>
              </div>
            ))}
            <button onClick={() => handleEdit(update)}>Edit</button>
            <button onClick={() => handleDelete(update._id)}>Delete</button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default WhatsNewDashboard;
